import { useState, useEffect } from "react";
import { MailCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { ModalCard } from "@/components/flowly/infra/ModalCard/ModalCard";
import { Button } from "@/components/flowly/infra/Button/Button";
import { toast } from "@/lib/toast";

const COOLDOWN_SECONDS = 60;

export function VerifyEmailModal() {
  const { showVerifyEmailModal, verifyEmailAddress, verifyEmailStartCooldown, closeVerifyEmailModal } =
    useAuthModal();
  const { t } = useLanguage();
  const [cooldown, setCooldown] = useState(0);

  // Set initial cooldown when modal opens, reset when it closes
  useEffect(() => {
    if (showVerifyEmailModal && verifyEmailStartCooldown) {
      setCooldown(COOLDOWN_SECONDS);
    } else if (!showVerifyEmailModal) {
      setCooldown(0);
    }
  }, [showVerifyEmailModal, verifyEmailStartCooldown]);

  // Countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;

    const interval = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldown]);

  const handleResend = async () => {
    setCooldown(COOLDOWN_SECONDS);

    const { error } = await supabase.auth.resend({
      type: "signup",
      email: verifyEmailAddress,
    });

    if (error) {
      toast.error(t.auth.resendError, { description: error.message });
    } else {
      toast.success(t.auth.verificationResent);
    }
  };

  const cooldownText =
    cooldown > 0
      ? t.auth.resendIn
          .replace("{seconds}", String(cooldown))
      : null;

  return (
    <ModalCard isOpen={showVerifyEmailModal} onClose={closeVerifyEmailModal}>
      <div className="text-center">
        {/* Icon */}
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <MailCheck className="w-10 h-10 text-primary" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t.auth.verifyEmailTitle}
        </h2>

        {/* Description */}
        <p className="text-muted-foreground mb-4">
          {t.auth.verifyEmailDescription}
        </p>

        {/* Email pill */}
        <div className="inline-block bg-secondary rounded-full px-4 py-2 mb-6">
          <span className="text-sm font-medium text-foreground" dir="ltr">
            {verifyEmailAddress}
          </span>
        </div>

        {/* Resend button */}
        <Button
          onClick={handleResend}
          variant="primary"
          className="w-full mb-3"
          disabled={cooldown > 0}
        >
          {cooldownText ?? t.auth.resendVerificationEmail}
        </Button>

        {/* Spam hint */}
        <p className="text-xs text-muted-foreground">
          {t.auth.checkSpamFolder}
        </p>
      </div>
    </ModalCard>
  );
}
